import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { OnboardingChrome } from '@/components/OnboardingChrome';
import { OnboardingResultsAreIn } from '@/pages/OnboardingResultsAreIn';

import { ClipboardList, SlidersHorizontal, PiggyBank } from 'lucide-react';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { cn } from '@client/lib';
import { motion } from 'framer-motion';
import { postAdvisorUpdatesByAdvisorIdRequest, selectClient, selectSessionAdvisor } from '@/model';

interface Step {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  isOptional?: boolean;
}

const STEPS: Step[] = [
  {
    number: 1,
    icon: <ClipboardList className="size-6 text-gray-700" />,
    title: 'Review Your Results',
    description:
      "We'll walk through your personalized long-term care predictions based on your health profile, including estimated care needs, duration, and costs.",
  },
  {
    number: 2,
    icon: <SlidersHorizontal className="size-6 text-gray-700" />,
    title: 'Personalize Your Plan',
    description:
      'Customize your care plan by selecting preferred care settings and adjusting family caregiving expectations for each phase of care.',
    isOptional: true,
  },
  {
    number: 3,
    icon: <PiggyBank className="size-6 text-gray-700" />,
    title: 'Explore Funding Options',
    description:
      'Discover how to finance your long-term care through various funding sources and see how they can cover your projected costs.',
  },
];

export function OnboardingOverview(_props: ClientIdProps) {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);
  const { clientFirstName } = useSelector(selectClient);

  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);
  const [visibleLines, setVisibleLines] = useState<number[]>([]);

  const overviewSlideDefault = advisor.defaultClientOnboardingOverviewSlide ?? true;
  const [dontShowOverview, setDontShowOverview] = useState(() => !overviewSlideDefault);

  function handleSavePreference() {
    dispatch(
      postAdvisorUpdatesByAdvisorIdRequest({
        ...advisor,
        defaultClientOnboardingOverviewSlide: !dontShowOverview,
      }),
    );
  }

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    const baseDelay = 150;
    const cardDuration = 350;
    const lineDuration = 350;

    STEPS.forEach((step, index) => {
      const cardDelay = baseDelay + index * (cardDuration + lineDuration);
      const cardTimer = setTimeout(() => {
        setVisibleSteps(prev => [...prev, step.number]);
      }, cardDelay);

      timers.push(cardTimer);

      if (index < STEPS.length - 1) {
        const lineDelay = cardDelay + cardDuration;
        const lineTimer = setTimeout(() => {
          setVisibleLines(prev => [...prev, step.number]);
        }, lineDelay);

        timers.push(lineTimer);
      }
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  return (
    <OnboardingChrome
      slidePage={OnboardingOverview}
      nextButtonLabel="Let's Get Started"
      nextSlidePage={OnboardingResultsAreIn}
      onNextClick={handleSavePreference}
      hideProgressStepper
    >
      <div className="flex flex-col items-center justify-center py-12 md:py-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-2 text-center"
        >
          <h1 className="text-balance text-3xl font-bold text-gray-900 md:text-4xl">
            Building {clientFirstName}&apos;s{' '}
            <span className="text-primary">Long-Term Care Plan</span>
          </h1>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-balance text-center text-base text-gray-700 md:text-lg"
        >
          Understand your care needs, tailor your plan, and see how to cover the costs.
        </motion.p>
        <div className="relative mt-12 w-full max-w-2xl md:mt-16">
          {STEPS.map((step, index) => {
            const isLastStep = index === STEPS.length - 1;
            const isStepVisible = visibleSteps.includes(step.number);

            return (
              <div
                key={step.number}
                className={cn('relative flex', {
                  'opacity-60': step.isOptional,
                })}
              >
                <div className="relative mr-4 flex flex-col items-center md:mr-6">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isStepVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                    transition={{
                      duration: 0.2,
                      type: 'spring',
                      stiffness: 250,
                      damping: 15,
                    }}
                    className="relative"
                  >
                    <div className="flex size-12 items-center justify-center rounded-xl border-2 border-gray-200 bg-white md:size-14">
                      {step.icon}
                    </div>
                    <div
                      className={cn(
                        'bg-primary absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full border-2 border-white text-xs font-semibold text-white',
                        {
                          'bg-gray-200 text-gray-700': step.isOptional,
                        },
                      )}
                    >
                      {step.number}
                    </div>
                  </motion.div>

                  {!isLastStep && (
                    <motion.div
                      initial={{ scaleY: 0 }}
                      animate={visibleLines.includes(step.number) ? { scaleY: 1 } : { scaleY: 0 }}
                      transition={{ duration: 0.1, ease: 'easeOut' }}
                      className="min-h-20 w-0.5 flex-1 origin-top bg-gray-200 md:min-h-16"
                    />
                  )}
                </div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={isStepVisible ? { opacity: 1, x: 0 } : { opacity: 0 }}
                  transition={{ duration: 0.3, delay: 0.15 }}
                  className="flex-1"
                >
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-gray-950 md:text-lg">
                      {step.title}
                    </h3>
                    {step.isOptional && <Badge variant="secondary">Optional</Badge>}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-gray-700 md:text-base">
                    {step.description}
                  </p>
                </motion.div>
              </div>
            );
          })}

          <label
            htmlFor="dont-show-overview"
            className="mt-12 flex cursor-pointer select-none items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-gray-700 md:mt-16 md:rounded-xl md:p-4"
          >
            <Checkbox
              id="dont-show-overview"
              checked={dontShowOverview}
              onCheckedChange={() => setDontShowOverview(current => !current)}
              className="size-[18px] md:size-5"
            />
            <span>Don't show this overview again</span>
          </label>
        </div>
      </div>
    </OnboardingChrome>
  );
}
